import React, { useState, useEffect } from 'react';
import { useAuthHeader } from 'react-auth-kit';
import axios from 'axios';
import { Modal, Container, Row, Col, ButtonToolbar, Button, Form, Alert} from 'react-bootstrap';
import * as Icon from 'react-feather';
import { PERMISSION_PAGES, buildDefaultPermissionsForRoles } from '../../../../config/roleConfig';

function DefaultModal(props) {
    const authHeader = useAuthHeader();
    const apiUrl = process.env.REACT_APP_URL;

        const [role, setRole] = useState([]);
        const [roleCommissions, setRoleCommissions] = useState({});
        const [operadorLocalCommission, setOperadorLocalCommission] = useState('');
        const [clients, setClients] = useState([]);
        const [selectedClientId, setSelectedClientId] = useState('');
        const [permissions, setPermissions] = useState(() => buildDefaultPermissionsForRoles([]));
        const [formError, setFormError] = useState('');
        const [missingFields, setMissingFields] = useState([]);

        const roleLabels = {
            '2': 'Operador',
            '3': 'Vendedor',
            '4': 'Tráfico',
            '5': 'Monitorista',
            '6': 'Compras',
            '7': 'Finanzas',
            '8': 'RRHH',
            '9': 'Facturación',
            '10': 'Taller',
            '11': 'Cliente'
        };

        const rolesToPermissionRoles = (roleIds) => roleIds.map(roleId => ({
            title: roleLabels[roleId]
        }));

        // Check if Cliente role (11) is selected
        const isClienteRoleSelected = role.includes('11');
        const isOperadorRoleSelected = role.includes('2');

        const requiredFieldLabels = {
            firstName: 'Nombre',
            lastName: 'Apellido',
            role: 'Rol',
            email: 'Email',
            password: 'Contraseña',
            phoneNumber: 'Celular',
            location: 'Sede',
            dob: 'Fecha de nacimiento',
            clientId: 'Cliente',
            rfc: 'RFC',
            num_licencia: 'No. Licencia',
            domicilio_fiscal_cp: 'C.P. Fiscal'
        };

        const backendToFormField = {
            first_name: 'firstName',
            last_name: 'lastName',
            roles: 'role',
            email: 'email',
            password: 'password',
            phone_number: 'phoneNumber',
            location: 'location',
            dob: 'dob',
            client_id: 'clientId',
            rfc: 'rfc',
            num_licencia: 'num_licencia',
            domicilio_fiscal_cp: 'domicilio_fiscal_cp'
        };

        const isFieldMissing = (fieldName) => missingFields.includes(fieldName);

        const getErrorMessage = (fieldName) => {
            if (!isFieldMissing(fieldName)) {
                return '';
            }

            return `${requiredFieldLabels[fieldName]} es obligatorio.`;
        };

        // Fetch clients when Cliente role is selected
        useEffect(() => {
            if (isClienteRoleSelected && clients.length === 0) {
                fetchClients();
            }
        }, [isClienteRoleSelected]);

        const fetchClients = () => {
            axios({
                method: 'GET',
                url: apiUrl + '/finance/get_all_clients',
                headers: {
                    Authorization: authHeader()
                }
            }).then(response => {
                if (response.data.msg && response.data.msg.Clients) {
                    setClients(response.data.msg.Clients);
                }
            }).catch(error => {
                console.log("Error fetching clients:", error);
            });
        };

        const handleRoleChange = (e) => {
            const selectedRoles = [].slice.call(e.target.selectedOptions).map(item => item.value);
            setRole(selectedRoles);
            setMissingFields(currentFields => currentFields.filter(fieldName => fieldName !== 'role'));
            setPermissions(buildDefaultPermissionsForRoles(rolesToPermissionRoles(selectedRoles)));

            // Remove commission entries for unselected roles
            const newRoleCommissions = {};
            selectedRoles.forEach(roleId => {
                newRoleCommissions[roleId] = roleCommissions[roleId] || 0;
            });
            setRoleCommissions(newRoleCommissions);

            // Reset Operador commission fields if Operador is deselected
            if (!selectedRoles.includes('2')) {
                setOperadorLocalCommission('');
            }

            // Reset client selection if Cliente role is deselected
            if (!selectedRoles.includes('11')) {
                setSelectedClientId('');
            }
        };

        const handleCommissionChange = (roleId, commission) => {
            setRoleCommissions(prev => ({
                ...prev,
                [roleId]: parseFloat(commission) || 0
            }));
        };

        const handlePermissionChange = (pageKey, action, checked) => {
            setPermissions(prev => prev.map(permission => {
                if (permission.page_key !== pageKey) {
                    return permission;
                }

                return {
                    ...permission,
                    [`can_${action}`]: checked
                };
            }));
        };

        const handleButtonClick = (event) => {
            // alert('Botón de Guardar presionado');
            event.preventDefault();
            setFormError('');
            const apiUrl = process.env.REACT_APP_URL;
            const form = event.currentTarget;
            const fieldsToValidate = [
                'firstName',
                'lastName',
                'role',
                'email',
                'password',
                'phoneNumber',
                'location',
                'dob'
            ];

            if (isClienteRoleSelected) {
                fieldsToValidate.push('clientId');
            }

            if (isOperadorRoleSelected) {
                fieldsToValidate.push('rfc', 'num_licencia', 'domicilio_fiscal_cp');
            }

            const nextMissingFields = fieldsToValidate.filter(fieldName => {
                if (fieldName === 'role') {
                    return role.length === 0;
                }
                if (fieldName === 'clientId') {
                    return !selectedClientId;
                }

                const field = form.elements[fieldName];
                return !field || !field.value.trim();
            });

            setMissingFields(nextMissingFields);

            if (nextMissingFields.length > 0) {
                setFormError(`Faltan campos obligatorios: ${nextMissingFields.map(fieldName => requiredFieldLabels[fieldName]).join(', ')}`);
                return;
            }

            // Create role-commission mapping for the API
            const rolesWithCommissions = role.map(roleId => {
                const roleData = {
                    role_id: roleId,
                    commission: roleCommissions[roleId] || 0
                };

                // Add Operador-specific local commission
                if (roleId === '2') {
                    roleData.local_commission = parseFloat(operadorLocalCommission) || 0;
                }

                return roleData;
            });

            const data = {
                'first_name': event.target.firstName.value,
                'last_name': event.target.lastName.value,
                'roles': role,
                'role_commissions': rolesWithCommissions,
                'permissions': permissions,
                'email': event.target.email.value,
                'password': event.target.password.value,
                'phone_number': event.target.phoneNumber.value,
                'location': event.target.location.value,
                'dob': event.target.dob.value,
                'salary': parseFloat(event.target.salary.value) || 0,
                'client_id': selectedClientId || null,
                'rfc': event.target.rfc.value || null,
                'num_licencia': event.target.num_licencia.value || null,
                'domicilio_fiscal_cp': event.target.domicilio_fiscal_cp.value || null
            }
            axios({
                method:'POST',
                url:apiUrl+'/users/signup',
            headers: {
                Authorization: authHeader()
            },
                data: data
            }).then(response => {
                console.log(response);
                props.onHide();
            })
            .catch(error => {
                console.log(error);
                const responseData = error.response?.data;
                const responseMissingFields = responseData?.missing_fields || [];
                const responseMissingFieldNames = responseMissingFields
                    .map(item => backendToFormField[item.field])
                    .filter(Boolean);

                setMissingFields(responseMissingFieldNames);
                setFormError(responseData?.error || 'No se pudo crear el usuario.');
            });
        }
        return (
            <Modal {...props} size="lg" aria-labelledby="contained-modal-title-vcenter">
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        Agregar Usuario
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Container>
                        {formError && (
                            <Alert variant="danger" dismissible onClose={() => setFormError('')}>
                                {formError}
                            </Alert>
                        )}
                        <Form noValidate onSubmit={handleButtonClick}>
                            <Row className="mb-3">
                                <Form.Group as={Col} controlId="firstName">
                                    <Form.Label>Nombre *</Form.Label>
                                    <Form.Control
                                        name="firstName"
                                        required
                                        isInvalid={isFieldMissing('firstName')}
                                        onChange={() => setMissingFields(currentFields => currentFields.filter(fieldName => fieldName !== 'firstName'))}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {getErrorMessage('firstName') || 'Nombre es obligatorio.'}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group as={Col} controlId="lastName">
                                    <Form.Label>Apellido *</Form.Label>
                                    <Form.Control
                                        name="lastName"
                                        required
                                        isInvalid={isFieldMissing('lastName')}
                                        onChange={() => setMissingFields(currentFields => currentFields.filter(fieldName => fieldName !== 'lastName'))}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {getErrorMessage('lastName') || 'Apellido es obligatorio.'}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group as={Col} controlId="role">
                                    <Form.Label>Rol *</Form.Label>
                                    <Form.Control
                                        as="select"
                                        multiple
                                        name="role"
                                        value={role}
                                        onChange={handleRoleChange}
                                        isInvalid={isFieldMissing('role')}
                                    >
                                        {/* <option value="1">Admin</option> */}
                                        <option value="2">Operador</option>
                                        <option value="3">Vendedor</option>
                                        <option value="4">Tráfico</option>
                                        <option value="5">Monitorista</option>
                                        <option value="6">Compras</option>
                                        <option value="7">Finanzas</option>
                                        <option value="8">RRHH</option>
                                        <option value="9">Facturación</option>
                                        <option value="10">Taller</option>
                                        <option value="11">Cliente</option>
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">
                                        {getErrorMessage('role') || 'Rol es obligatorio.'}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Row>

                            {/* Client selection dropdown - only shown when Cliente role is selected */}
                            {isClienteRoleSelected && (
                                <Row className="mb-3">
                                    <Form.Group as={Col} controlId="clientId">
                                        <Form.Label>Asignar a Cliente *</Form.Label>
                                        <Form.Select
                                            name="clientId"
                                            value={selectedClientId}
                                            onChange={(e) => {
                                                setSelectedClientId(e.target.value);
                                                setMissingFields(currentFields => currentFields.filter(fieldName => fieldName !== 'clientId'));
                                            }}
                                            required={isClienteRoleSelected}
                                            isInvalid={isFieldMissing('clientId')}
                                        >
                                            <option value="">Seleccionar cliente...</option>
                                            {clients.map(client => (
                                                <option key={client.id} value={client.id}>
                                                    {client.company_name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Text className="text-muted">
                                            Este usuario solo podrá ver información de este cliente.
                                        </Form.Text>
                                        <Form.Control.Feedback type="invalid">
                                            {getErrorMessage('clientId') || 'Cliente es obligatorio.'}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Row>
                            )}

                            <Row className="mb-3">
                                <Form.Group as={Col} controlId="email">
                                    <Form.Label>Email *</Form.Label>
                                    <Form.Control
                                        name="email"
                                        type="email"
                                        required
                                        isInvalid={isFieldMissing('email')}
                                        onChange={() => setMissingFields(currentFields => currentFields.filter(fieldName => fieldName !== 'email'))}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {getErrorMessage('email') || 'Email es obligatorio.'}
                                    </Form.Control.Feedback>
                                </Form.Group><Form.Group as={Col} controlId="password">
                                    <Form.Label>Contraseña *</Form.Label>
                                    <Form.Control
                                        name="password"
                                        type="password"
                                        required
                                        isInvalid={isFieldMissing('password')}
                                        onChange={() => setMissingFields(currentFields => currentFields.filter(fieldName => fieldName !== 'password'))}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {getErrorMessage('password') || 'Contraseña es obligatoria.'}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group as={Col} controlId="phoneNumber">
                                    <Form.Label>Celular *</Form.Label>
                                    <Form.Control
                                        name="phoneNumber"
                                        required
                                        isInvalid={isFieldMissing('phoneNumber')}
                                        onChange={() => setMissingFields(currentFields => currentFields.filter(fieldName => fieldName !== 'phoneNumber'))}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {getErrorMessage('phoneNumber') || 'Celular es obligatorio.'}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Row>
                            <Row className="mb-3">
                                <Form.Group as={Col} controlId="location">
                                    <Form.Label>Sede *</Form.Label>
                                    <Form.Select
                                        name="location"
                                        required
                                        defaultValue="Guadalajara, Jalisco"
                                        isInvalid={isFieldMissing('location')}
                                        onChange={() => setMissingFields(currentFields => currentFields.filter(fieldName => fieldName !== 'location'))}
                                    >
                                        <option>Guadalajara, Jalisco</option>
                                        <option>Monterrery, Nuevo Leon</option>
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {getErrorMessage('location') || 'Sede es obligatoria.'}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group as={Col} controlId="dob">
                                    <Form.Label>Fecha de nacimiento *</Form.Label>
                                    <Form.Control
                                        name="dob"
                                        type="date"
                                        required
                                        placeholder="1990-01-01"
                                        isInvalid={isFieldMissing('dob')}
                                        onChange={() => setMissingFields(currentFields => currentFields.filter(fieldName => fieldName !== 'dob'))}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {getErrorMessage('dob') || 'Fecha de nacimiento es obligatoria.'}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Row>
                            <Row className="mb-3">
                                <h6>SAT / Carta Porte (Operadores)</h6>
                            </Row>
                            <Row className="mb-3">
                                <Form.Group as={Col} controlId="rfc">
                                    <Form.Label>RFC{isOperadorRoleSelected ? ' *' : ''}</Form.Label>
                                    <Form.Control
                                        name="rfc"
                                        placeholder="XAXX010101000"
                                        required={isOperadorRoleSelected}
                                        isInvalid={isFieldMissing('rfc')}
                                        onChange={() => setMissingFields(currentFields => currentFields.filter(fieldName => fieldName !== 'rfc'))}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {getErrorMessage('rfc') || 'RFC es obligatorio.'}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group as={Col} controlId="num_licencia">
                                    <Form.Label>No. Licencia{isOperadorRoleSelected ? ' *' : ''}</Form.Label>
                                    <Form.Control
                                        name="num_licencia"
                                        placeholder="Número de licencia"
                                        required={isOperadorRoleSelected}
                                        isInvalid={isFieldMissing('num_licencia')}
                                        onChange={() => setMissingFields(currentFields => currentFields.filter(fieldName => fieldName !== 'num_licencia'))}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {getErrorMessage('num_licencia') || 'No. Licencia es obligatorio.'}
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group as={Col} controlId="domicilio_fiscal_cp">
                                    <Form.Label>C.P. Fiscal{isOperadorRoleSelected ? ' *' : ''}</Form.Label>
                                    <Form.Control
                                        name="domicilio_fiscal_cp"
                                        placeholder="44100"
                                        required={isOperadorRoleSelected}
                                        isInvalid={isFieldMissing('domicilio_fiscal_cp')}
                                        onChange={() => setMissingFields(currentFields => currentFields.filter(fieldName => fieldName !== 'domicilio_fiscal_cp'))}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {getErrorMessage('domicilio_fiscal_cp') || 'C.P. Fiscal es obligatorio.'}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Row>
                            <Row className="mb-3">
                                <Form.Group as={Col} controlId="salary">
                                    <Form.Label>Salario Base</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="$0.00"
                                        pattern="[0-9.,]+"
                                    />
                                </Form.Group>
                            </Row>
                            {/* Commission inputs for each selected role */}
                            {role.length > 0 && (
                                <Row className="mb-3">
                                    <Col>
                                        <Form.Label><strong>Comisiones por Rol</strong></Form.Label>
                                        {role.map(roleId => (
                                            <Row key={roleId} className="mb-2">
                                                {roleId === '2' ? (
                                                    <>
                                                        <Form.Group as={Col} controlId={`commission-${roleId}`}>
                                                            <Form.Label>{roleLabels[roleId]} - Comisión Foránea (%)</Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                min="0"
                                                                max="100"
                                                                step="0.1"
                                                                placeholder="0.0%"
                                                                value={roleCommissions[roleId] || ''}
                                                                onChange={(e) => handleCommissionChange(roleId, e.target.value)}
                                                            />
                                                        </Form.Group>
                                                        <Form.Group as={Col} controlId="operadorLocalCommission">
                                                            <Form.Label>{roleLabels[roleId]} - Comisión Local ($)</Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                placeholder="$0.00"
                                                                value={operadorLocalCommission}
                                                                onChange={(e) => setOperadorLocalCommission(e.target.value)}
                                                            />
                                                        </Form.Group>
                                                    </>
                                                ) : (
                                                    <Form.Group as={Col} controlId={`commission-${roleId}`}>
                                                        <Form.Label>{roleLabels[roleId]} - Comisión (%)</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            min="0"
                                                            max="100"
                                                            step="0.1"
                                                            placeholder="0.0%"
                                                            value={roleCommissions[roleId] || ''}
                                                            onChange={(e) => handleCommissionChange(roleId, e.target.value)}
                                                        />
                                                    </Form.Group>
                                                )}
                                            </Row>
                                        ))}
                                    </Col>
                                </Row>
                            )}
                            <Row className="mb-3">
                                <Col>
                                    <Form.Label><strong>Permisos por Página</strong></Form.Label>
                                    <div className="table-responsive">
                                        <table className="table table-sm align-middle">
                                            <thead>
                                                <tr>
                                                    <th>Página</th>
                                                    <th>Leer</th>
                                                    <th>Escribir</th>
                                                    <th>Eliminar</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {PERMISSION_PAGES.map(page => {
                                                    const permission = permissions.find(item => item.page_key === page.pageKey) || {};
                                                    return (
                                                        <tr key={page.pageKey}>
                                                            <td>
                                                                <div>{page.label}</div>
                                                                <small className="text-muted">{page.sectionTitle}</small>
                                                            </td>
                                                            {['read', 'write', 'delete'].map(action => (
                                                                <td key={action}>
                                                                    <Form.Check
                                                                        type="checkbox"
                                                                        checked={Boolean(permission[`can_${action}`])}
                                                                        onChange={(e) => handlePermissionChange(page.pageKey, action, e.target.checked)}
                                                                        aria-label={`${page.label} ${action}`}
                                                                    />
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </Col>
                            </Row>
                            {/* <Row className="mb-3">
                            <Form.Group controlId="formFile" className="mb-3">
                                <Form.Label>Foto</Form.Label>
                                <Form.Control type="file" />
                            </Form.Group>
                            </Row> */}
                            <Button type="submit" value="Submit">Guardar</Button>
                        </Form>
                    </Container>
                </Modal.Body>
            </Modal>
        );

}

class ModalWithGrid extends React.Component {
    constructor(...args) {
        super(...args);
        this.state = { modalShow: false };
    }

    render() {
        // let modalClose = () => this.setState({ modalShow: false });
        let add_new_user = () => {this.props.setData(); this.setState({ modalShow: false });}
        return (
            <ButtonToolbar>
                <Button
                    variant="primary"
                    onClick={() => this.setState({ modalShow: true })}
                >
                    Agregar Usuario
                </Button>
                <DefaultModal show={this.state.modalShow} onHide={add_new_user} />
            </ButtonToolbar>
        );
    }
}

export default ModalWithGrid;
